<script lang="tsx">
  import {
    defineComponent, PropType, ref, watch, computed,
  } from 'vue'
  import { BeerStyleObj } from '@xhs/yam-beer/core'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import toaster from '@xhs/yam-beer/cmp/common/Toast/Toaster'

  import { useFileUpload, FileUploadOptions } from './useFileUpload'
  import {
    initFileList,
    UploaderType,
    postXhsItemGenerator,
    getFileValidator,
    BeerFileItem,
    CheckFn,
    ValidateParams,
    CreateUploaderOptions,
    InputFile,
  } from './utils'
  import FileItem from './FileItem.vue'
  import initStyle from './style'

  initStyle()

type CheckFnProp = CheckFn | CheckFn[]
  /**
   * 上传的通用组件，用于封装其他的如 image、file 上传
   *
   * 处理了：
   * - ghost file input，触发文件选择器
   * - 处理上传中，上传重试的操作
   *
   * 你可以用这个来封装自己的上传组件
   */
  export default defineComponent({
    name: 'BeerFileUpload',
    props: {
      type: {
        type: String as PropType<UploaderType>,
        default: 'file',
      },
      modelValue: {
        type: Array as PropType<BeerFileItem[]>,
        default: () => [],
      },
      disabled: {
        type: Boolean,
      },
      readonly: {
        type: Boolean,
      },
      /**
       * 外层容器样式
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 可接受的文件
       *
       * 在 Safari 中，若要选择 mp4 类型的文件，需要设置 accept="video/mp4,video/x-m4v,video/*"
       *
       * @see MDN-accept https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept
       * @see MDN-mime-types https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types
       * @see safari-input-type-file-accept-video-ignores-mp4-files https://stackoverflow.com/questions/19107685/safari-input-type-file-accept-video-ignores-mp4-files
       */
      accept: {
        type: [String, Array] as PropType<string | string[]>,
      },
      maxFiles: {
        type: Number as PropType<number>,
        default: 1,
      },
      maxSize: {
        type: Number as PropType<number>,
        default: Infinity,
      },
      /**
       * 上传的桶子
       */
      bucket: {
        type: String,
        required: true,
      },
      /**
       * 上传的业务线
       */
      bizKey: {
        type: String,
        required: true,
      },
      /**
       * 验证 Input 的 File
       * @type {Function | Array<Function>}
       */
      validateFile: {
        type: [Function, Array] as PropType<CheckFnProp>,
      },
      /**
       * 预置的校验逻辑
       */
      validateParams: {
        type: Object as PropType<Partial<ValidateParams>>,
        default: () => ({}),
      },
      /**
       * 自定义处理上传的结果，reject 来阻止添加 value
       */
      handleResult: {
        type: Function as PropType<(item: BeerFileItem) => BeerFileItem>,
      },
      /**
       * 每个文件上传成功的 callback
       */
      handlePostSuccess: {
        type: Function as PropType<(item: BeerFileItem) => void>,
      },
      /**
       * useUpload 的参数
       */
      uploadParams: {
        type: Object as PropType<Omit<FileUploadOptions, 'accept'|'maxFiles'|'onDrop'>>,
        default: () => ({}),
      },
      /**
       * @xhs/uploader  constructor 和 post 里面支持的参数
       */
      uploaderParams: {
        type: Object as PropType<Pick<CreateUploaderOptions, 'keyPattern'|'useAccelerate'|'qiniuParam'>>,
        default: () => ({}),
      },
      /**
       * @xhs/uploader 里面支持的参数, 当需要 file 参数来获取的时候使用
       */
      getUploaderParams: {
        type: Function as PropType<(file: InputFile|undefined) => Pick<CreateUploaderOptions, 'keyPattern'|'useAccelerate'|'qiniuParam'>>,
        default: () => ({}),
      },
      /**
       * 上传文件的错误处理，默认会toaster 提示
       */
      onErrorReject: {
        type: Function as PropType<(reason: string) => void>,
        required: false,
      },
      /**
       * 上传文件的点击，默认会新建窗口打开
       */
      handleClickFile: {
        type: Function,
        required: false,
      },
      getPostJob: {
        type: Function,
        default: ({
          type, bucket, bizKey,
          }: {
          type: UploaderType
          bucket: string
          bizKey: string
        }) => postXhsItemGenerator({
          type,
          bucket,
          bizKey,
        }),
        required: false,
      },
    },
    emits: ['update:modelValue', 'remove:modelValue'],
    setup(props, { slots, emit }) {
      const localValue = ref<BeerFileItem[]>([])
      watch(() => props.modelValue, val => {
        const idList = localValue.value.map(i => i.id)
        if (val.some(i => idList.indexOf(i.id) < 0)) {
          localValue.value = initFileList(
            val.filter(i => i.url),
          )
        } else if (val?.length > localValue.value.length) {
          localValue.value = initFileList(
            val.filter(i => i.url),
          )
        } else if (val.length === 0) {
          localValue.value = []
        }
      }, {
        immediate: true,
      })

      const createFileItem = props.getPostJob({
        type: props.type,
        bucket: props.bucket,
        bizKey: props.bizKey,
      })

      const validate = getFileValidator({
        type: props.type,
        ...props.validateParams,
        customCheck: props.validateFile,
      })

      function handleRemove(item: BeerFileItem) {
        localValue.value = localValue.value.filter(i => i.id !== item.id)
        emit('remove:modelValue', item)
        emit('update:modelValue', localValue.value)
      }

      function handleAdd(item: BeerFileItem) {
        localValue.value = [
          ...localValue.value,
          item,
        ]
        emit('update:modelValue', localValue.value)
      }

      function handleReject(reason: string) {
        if (props.onErrorReject) {
          props.onErrorReject(reason)
        } else {
          toaster.danger(reason)
        }
      }

      function handleFileClick(e: Event, fileItem: BeerFileItem) {
        if (props.handleClickFile) {
          props.handleClickFile(fileItem)
        } else if (fileItem.url) {
          window.open(
            fileItem.url.indexOf('http') > -1
              ? fileItem.url
              : `http:${fileItem.url}`,
            '_blank',
          )
        }
      }
      const {
        getRootProps,
        getInputProps,
      } = useFileUpload({
        // @ts-ignore
        maxSize: props.maxSize,
        accept: props.accept,
        maxFiles: props.maxFiles,
        ...props.uploadParams,
        disabled: props.disabled || props.readonly,
        onDrop: acceptedFiles => {
          acceptedFiles.forEach(file => {
            validate(file)
              .then(() => {
                const item = createFileItem(file)
                if (props.handleResult) {
                  return props.handleResult(item)
                }
                return item
              })
              .then(item => {
                item.post?.({
                  ...props.uploaderParams,
                  ...props.getUploaderParams(file),
                })
                  .then(() => {
                    props.handlePostSuccess?.(item)
                  })
                handleAdd(item)
              })
              .catch(e => {
                handleReject(e.message)
              })
          })
        },
        onDropRejected: rejectReasons => {
          rejectReasons.forEach(i => handleReject(i.errors[0]?.message))
        },
      })

      const tipsText = computed(() => {
        const texts = []
        if (props.accept) {
          texts.push(`支持 ${props.accept} 类型`)
        }
        if (props.maxFiles && props.maxFiles > 0) {
          texts.push(`最多上传 ${props.maxFiles} 个`)
        }
        if (texts.length < 1) {
          return '请点击按钮上传文件'
        }
        return texts.join(', ')
      })

      return () => {
        const rootProps = getRootProps()
        const inputProps = getInputProps()
        let ControlCmps = null
        if (!props.readonly && props.maxFiles > localValue.value?.length) {
          ControlCmps = [
          <input {...inputProps} />,
          slots.default ? slots.default({
            tipsText,
            rootProps,
          }) : [
            <BeerButton
              disabled={props.disabled}
              variant="secondary"
              {...rootProps}
            >
              <BeerIcon
                size={12}
                color={null}
                bs={{ mr: 4 }}
                icon="uploadDnd"
                viewBox="0 0 30 33"
              />
              选择文件
            </BeerButton>,
            <NewBsBox
              bs={{
                'cmp.fileUpload': 'btnTips',
              }}
            >
              {slots['default-tips']
                ? slots['default-tips']()
                : tipsText.value }
            </NewBsBox>,
          ],
          ]
        }
        return (
          <NewBsBox bs={props.bs}>
            { ControlCmps }
            {slots.items ? slots.items({ list: localValue.value, handleRemove }) : (
              <NewBsBox
                bs={{
                  width: props.bs.width ? props.bs.width : 400,
                }}
              >
                {localValue.value.map(file => {
                  const { id } = file
                  return (
                    <FileItem
                      key={id}
                      readonly={props.readonly}
                      handleRetry={() => file.post?.(props.getUploaderParams(file.file))}
                      uploadPercent={file.uploadPercent}
                      handleClick={(e: Event) => handleFileClick(e, file)}
                      handleRemove={() => handleRemove(file)}
                      filename={file.filename}
                      error={file.error}
                      src={file.url}
                    />
                  )
                })}
              </NewBsBox>
            )}
          </NewBsBox>
        )
      }
    },
  })
</script>
